import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, ShieldCheck, FlaskConical, HeartPulse, ArrowRight, Sparkles, Search } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const pillars = [
  { icon: Leaf, title: "Like Cures Like", text: "Natural substances chosen to gently match and resolve your symptoms." },
  { icon: FlaskConical, title: "Minimum Dose", text: "Carefully potentised remedies using the smallest effective dose." },
  { icon: HeartPulse, title: "Individualized Care", text: "Treatment tailored to the whole person — body, mind and emotion." },
];

const why = [
  { icon: Leaf, title: "Pure Plant Sources", text: "Botanical & mineral origins, thoughtfully sourced." },
  { icon: ShieldCheck, title: "Gentle & Non-Habit Forming", text: "Safe remedies for the whole family." },
  { icon: FlaskConical, title: "Quality Assured", text: "Prepared with strict quality control at every step." },
  { icon: HeartPulse, title: "Expert Practitioners", text: "Guided by experienced homeopathic doctors." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [slide, setSlide] = useState(0);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Admin can choose specific slider images from the dashboard (Home Slider
  // tab). Until they do, fall back to featured products so the hero never
  // looks empty on a fresh install.
  const configuredSlides = (homeContent?.slides || []).filter((s) => s.image_url);
  const slides = configuredSlides.length > 0
    ? configuredSlides.map((s, i) => ({ ...s, key: `s-${i}` }))
    : products
        .filter((p) => p.image_url)
        .map((p) => ({ image_url: p.image_url, title: p.name, subtitle: p.category, link: `/products/${p.id}`, key: `p-${p.id}` }));

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    navigate(q ? `/products?q=${encodeURIComponent(q)}` : "/products");
  };

  useEffect(() => {
    api.get("/products", { params: { featured: "true" } })
      .then(({ data }) => setProducts(data.slice(0, 6)))
      .catch(() => {});
    api.get("/content/home").then(({ data }) => setHomeContent(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-secondary/60 to-background" />
        <div className="container-xl grid items-center gap-12 py-16 md:py-24 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="eyebrow inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-primary shadow-sm ring-1 ring-border">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Certified Homeopathic Quality
            </span>
            <h1 className="mt-6 text-4xl font-700 leading-[1.05] text-foreground sm:text-5xl lg:text-6xl">
              Gentle healing, <span className="text-[var(--brand-navy)]">rooted in nature.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              ECONOMIC H PHARMACY (EHP) brings you pure, natural homeopathic remedies crafted with classical
              principles and eco-conscious care — supporting your body's own path to wellness.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/products" className="btn-primary" data-testid="hero-explore-button">
                Explore Our Products <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="hero-consult-button">
                Book a Consultation
              </Link>
            </div>
            <form onSubmit={submitSearch} className="mt-8 flex max-w-xl items-center gap-2" data-testid="home-search-form">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search remedies — immunity, sleep, skin..."
                  className="input-field bg-white !pl-12"
                  data-testid="home-search-input"
                />
              </div>
              <button type="submit" className="btn-primary shrink-0 !px-6" data-testid="home-search-button">
                Search
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl ring-1 ring-border" data-testid="hero-product-slider">
              {slides.length === 0 ? (
                <img
                  src="https://images.unsplash.com/photo-1512867957657-38dbae50a35b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                  alt="Homeopathic remedy vials"
                  className="h-[420px] w-full object-cover"
                />
              ) : (
                <>
                  {slides.map((s, i) => (
                    <Link
                      key={s.key}
                      to={s.link || "/products"}
                      className={`transition-opacity duration-1000 ${i === slide ? "opacity-100" : "pointer-events-none opacity-0"} ${i === 0 ? "relative block" : "absolute inset-0 block"}`}
                      data-testid={`hero-slide-${i}`}
                    >
                      <img src={assetUrl(s.image_url)} alt={s.title || "EHP"} className="h-[420px] w-full object-cover" />
                      {(s.subtitle || s.title) && (
                        <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent px-6 pb-16 pt-5">
                          {s.subtitle && <div className="text-xs font-semibold uppercase tracking-wide text-white/70">{s.subtitle}</div>}
                          {s.title && <div className="font-serif text-xl text-white">{s.title}</div>}
                        </div>
                      )}
                    </Link>
                  ))}
                  <div className="absolute bottom-5 right-6 flex gap-2" data-testid="hero-slider-dots">
                    {slides.map((s, i) => (
                      <button
                        key={s.key}
                        onClick={() => setSlide(i)}
                        aria-label={`Show slide ${i + 1}`}
                        data-testid={`hero-slider-dot-${i}`}
                        className={`h-2 rounded-full transition-all duration-300 ${i === slide ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-xl ring-1 ring-border sm:block">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-accent/15 p-3"><Leaf className="h-6 w-6 text-accent" /></div>
                <div>
                  <div className="font-serif text-xl text-[var(--brand-navy)]">100%</div>
                  <div className="text-xs text-muted-foreground">Natural Botanical Base</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section-pad bg-secondary/40" data-testid="home-products-section">
        <div className="container-xl">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <span className="eyebrow text-accent">Our Remedies</span>
              <h2 className="mt-3 text-3xl font-700 sm:text-4xl">Featured products</h2>
            </div>
            <Link to="/products" className="btn-outline" data-testid="home-view-all-products">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {products.length === 0 ? (
            <p className="mt-10 text-muted-foreground">No products available yet.</p>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* WHY EHP */}
      <section className="section-pad">
        <div className="container-xl">
          <div className="max-w-2xl">
            <span className="eyebrow text-accent">Why EHP</span>
            <h2 className="mt-3 text-3xl font-700 sm:text-4xl">A modern approach to timeless healing</h2>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {why.map((w, i) => (
              <div key={w.title} className="rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:shadow-lg animate-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                <div className="inline-flex rounded-xl bg-secondary p-3 text-primary"><w.icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-serif text-lg">{w.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOMEOPATHY TEASER */}
      <section className="section-pad">
        <div className="container-xl grid items-center gap-12 lg:grid-cols-2">
          <div className="overflow-hidden rounded-[2rem] shadow-xl ring-1 ring-border">
            <img
              src="https://images.unsplash.com/photo-1567922045116-2a00fae2ed03?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Botanical leaves"
              className="h-[380px] w-full object-cover"
            />
          </div>
          <div>
            <span className="eyebrow text-accent">The Science</span>
            <h2 className="mt-3 text-3xl font-700 sm:text-4xl">What makes homeopathy work</h2>
            <p className="mt-4 text-muted-foreground">
              Homeopathy stimulates the body's natural healing response using highly diluted natural
              substances. Here are the three principles at its heart.
            </p>
            <div className="mt-8 space-y-4">
              {pillars.map((p) => (
                <div key={p.title} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent"><p.icon className="h-5 w-5" /></div>
                  <div>
                    <h3 className="font-serif text-lg">{p.title}</h3>
                    <p className="text-sm text-muted-foreground">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/about-homeopathy" className="btn-primary mt-8" data-testid="home-learn-homeopathy">
              Learn more <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
